import React from 'react';
import { BrowserRouter as Router, Routes, Route, Link } from 'react-router-dom';
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsAndConditions from './components/TermsAndConditions';
import Support from './components/Support';

function Home() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation */}
      <nav className="bg-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <span className="text-2xl font-bold text-primary"><img src="kovaLogo.png" alt="Kova Logo" className="w-8 h-8" /></span>
            </div>
            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-4">
              <Link to="/support" className="text-gray-600 hover:text-primary">Support</Link>
              <Link to="/privacy-policy" className="text-gray-600 hover:text-primary">Privacy Policy</Link>
              <Link to="/terms" className="text-gray-600 hover:text-primary">Terms & Conditions</Link>
            </div>
            {/* Mobile Navigation Button */}
            <div className="md:hidden flex items-center">
              <button
                type="button"
                className="text-gray-500 hover:text-primary focus:outline-none"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
            </div>
          </div>
        </div>
        {/* Mobile Navigation Menu */}
        <div className={`md:hidden ${isMobileMenuOpen ? 'block' : 'hidden'}`}>
          <div className="px-2 pt-2 pb-3 space-y-1">
            <Link to="/support" className="block px-3 py-2 text-gray-600 hover:text-primary">Support</Link>
            <Link to="/privacy-policy" className="block px-3 py-2 text-gray-600 hover:text-primary">Privacy Policy</Link>
            <Link to="/terms" className="block px-3 py-2 text-gray-600 hover:text-primary">Terms & Conditions</Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <div className="picture max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center picture">
          <h1 className="text-4xl font-extrabold text-primary sm:text-5xl md:text-6xl">
            Secure E-commerce with
            <span className="text-[#F5BC57]"> Escrow Protection</span>
          </h1>
          <p className="mt-3 max-w-md mx-auto text-base text-gray-500 sm:text-lg md:mt-5 md:text-xl md:max-w-3xl">
            Shop with confidence knowing your transactions are protected by our advanced escrow system.
            Your money is held securely until you receive your order.
          </p>
          <div className="mt-5 max-w-md mx-auto sm:flex sm:justify-center md:mt-8 space-y-4 sm:space-y-0 sm:space-x-4">
            <div className="rounded-md shadow">
              <a
                href="https://play.google.com/store"
                className="w-full flex items-center justify-center px-8 py-3 border border-transparent text-base font-medium rounded-md text-white bg-primary hover:bg-secondary md:py-4 md:text-lg md:px-10"
              >
                <svg className="w-8 h-8 mr-2" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.6 9.48l1.84-3.18c.16-.31.04-.69-.26-.85-.29-.15-.65-.06-.83.22l-1.88 3.24c-2.86-1.21-6.08-1.21-8.94 0L5.65 5.67c-.19-.29-.58-.38-.87-.2-.28.18-.37.54-.22.83L6.4 9.48C3.3 11.25 1.28 14.44 1 18h22c-.28-3.56-2.3-6.75-5.4-8.52zM7 15.25c-.69 0-1.25-.56-1.25-1.25s.56-1.25 1.25-1.25 1.25.56 1.25 1.25-.56 1.25-1.25 1.25zm10 0c-.69 0-1.25-.56-1.25-1.25s.56-1.25 1.25-1.25 1.25.56 1.25 1.25-.56 1.25-1.25 1.25z"/>
                </svg>
                Android
              </a>
            </div>
            <div className="rounded-md shadow">
              <a
                href="https://applestore.com"
                className="w-full flex items-center justify-center px-8 py-3 border border-transparent text-base font-medium rounded-md text-white bg-primary hover:bg-secondary md:py-4 md:text-lg md:px-10"
              >
                <svg className="w-8 h-8 mr-2" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                </svg>
                App Store
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div className="bg-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
            <div className="text-center">
              <div className="text-primary text-4xl mb-4">🔒</div>
              <h3 className="text-lg font-medium text-gray-900">Secure Escrow</h3>
              <p className="mt-2 text-base text-gray-500">
                Your payment is held securely until you confirm receipt of your order.
              </p>
            </div>
            <div className="text-center">
              <div className="text-primary text-4xl mb-4">🛡️</div>
              <h3 className="text-lg font-medium text-gray-900">Buyer Protection</h3>
              <p className="mt-2 text-base text-gray-500">
                Full refund if the product doesn't match the description.
              </p>
            </div>
            <div className="text-center">
              <div className="text-primary text-4xl mb-4">⚡</div>
              <h3 className="text-lg font-medium text-gray-900">Fast Processing</h3>
              <p className="mt-2 text-base text-gray-500">
                Quick and efficient transaction processing.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-800">
        <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center space-x-6">
            <Link to="/privacy-policy" className="text-gray-400 hover:text-white">Privacy Policy</Link>
            <Link to="/terms" className="text-gray-400 hover:text-white">Terms & Conditions</Link>
          </div>
          <p className="mt-8 text-center text-base text-gray-400">
            &copy; 2025 Kova. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}

function App() {
  return (
    <Router>
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsAndConditions />} />
        <Route path="/support" element={<Support />} />
      </Routes>
    </Router>
  );
}

export default App; 